import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useSectors } from "@/hooks/useSectors";

export interface SectorComboboxProps {
  /** Current sector value (free text — not constrained to project list). */
  value: string | null;
  onChange: (next: string | null) => void;
  /** Project slug used to fetch the distinct sector list. */
  slug: string;
  /** Trigger placeholder when value is empty. */
  placeholder?: string;
  /** Disable interaction (read-only display). */
  disabled?: boolean;
  /** Show a small spinner on the trigger (for async parents). */
  saving?: boolean;
  /** Customise trigger height — defaults to h-9 to match Input. */
  triggerClassName?: string;
  /** Width of the popover panel. */
  contentClassName?: string;
  /** Allow clearing the field. Off by default — opt in where it makes
   *  sense (filters). */
  clearable?: boolean;
}

export function SectorCombobox({
  value,
  onChange,
  slug,
  placeholder = "Select sector…",
  disabled,
  saving,
  triggerClassName,
  contentClassName,
  clearable,
}: SectorComboboxProps) {
  const { sectors, loading } = useSectors(slug);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  // Always show the current value as a selectable row even if the
  // backend hasn't observed it yet (just-typed value, project sample
  // didn't include it).
  const merged = useMemo(() => {
    const set = new Set(sectors);
    if (value && value.trim() !== "") set.add(value.trim());
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }, [sectors, value]);

  const trimmed = search.trim();
  const filtered = useMemo(() => {
    if (trimmed === "") return merged;
    const q = trimmed.toLowerCase();
    return merged.filter((s) => s.toLowerCase().includes(q));
  }, [merged, trimmed]);

  const exactMatch = merged.some(
    (s) => s.toLowerCase() === trimmed.toLowerCase(),
  );

  function pick(next: string | null) {
    onChange(next);
    setOpen(false);
    setSearch("");
  }

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "group flex h-9 w-full min-w-0 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-left text-sm font-normal shadow-sm transition-colors hover:border-slate-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60",
            triggerClassName,
          )}
        >
          <span
            className={cn(
              "min-w-0 flex-1 truncate",
              !value && "text-slate-400",
            )}
          >
            {value && value.trim() !== "" ? value : placeholder}
          </span>
          {saving ? (
            <Loader2 className="h-3 w-3 shrink-0 animate-spin text-slate-400" />
          ) : (
            <ChevronsUpDown className="h-3 w-3 shrink-0 text-slate-400 group-hover:text-slate-500" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-[260px] p-2", contentClassName)}
        align="start"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <Input
          ref={inputRef}
          placeholder="Search or type new…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 text-sm mb-2"
          onKeyDown={(e) => {
            if (e.key === "Enter" && trimmed && !exactMatch) {
              e.preventDefault();
              pick(trimmed);
            }
          }}
        />
        <div className="max-h-[220px] overflow-y-auto space-y-0.5">
          {clearable && value && (
            <button
              type="button"
              onClick={() => pick(null)}
              className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm text-slate-500 hover:bg-slate-50"
            >
              <span>Clear sector</span>
            </button>
          )}
          {filtered.length === 0 && !trimmed && loading && (
            <div className="px-2 py-1.5 text-sm text-slate-400">
              Loading…
            </div>
          )}
          {filtered.length === 0 && !trimmed && !loading && (
            <div className="px-2 py-1.5 text-sm text-slate-400">
              No sectors yet
            </div>
          )}
          {filtered.map((sector) => {
            const selected = value === sector;
            return (
              <button
                key={sector}
                type="button"
                onClick={() => pick(sector)}
                className={cn(
                  "flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-50",
                  selected && "bg-indigo-50 text-indigo-600",
                )}
              >
                <span className="min-w-0 truncate">{sector}</span>
                {selected && <Check className="h-3 w-3 shrink-0" />}
              </button>
            );
          })}
          {trimmed && !exactMatch && (
            <button
              type="button"
              onClick={() => pick(trimmed)}
              className="flex w-full items-center gap-1 rounded px-2 py-1.5 text-left text-sm text-indigo-600 hover:bg-indigo-50"
            >
              + Create &ldquo;{trimmed}&rdquo;
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
